import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../api/auth';
import { securitySettingsAPI } from '../api/securitySettings';
import { useTheme } from './ThemeContext';
import { toast } from 'react-toastify';
import { clearHubSession, leaveSignedOut, markHubSession } from '../utils/hubSession';

/** Sent on window when the signed-in person or business changes. */
export const SESSION_CHANGED = 'bms-session-changed';

const AuthContext = createContext(null);

const DEFAULT_SESSION_TIMEOUT = 120; // minutes

// A new sign-in starts a new inactivity clock. Without this, the last activity of an earlier
// session (a tab closed hours ago) made a fresh sign-in look idle and signed it straight out.
const startActivityClock = () => localStorage.setItem('lastActivity', Date.now().toString());

/**
 * Arriving from One Manager (/sso) starts a new session: whatever an earlier one left in this
 * browser goes first, before any part of the app can call the API with it. Otherwise its
 * expired token was refused while the new sign-in was still under way, and ended it.
 */
const dropLeftoverSessionOnSso = () => {
  if (typeof window === 'undefined' || !window.location.pathname.startsWith('/sso')) return;
  for (const key of ['accessToken', 'refreshToken', 'user', 'tenant', 'activeModule', 'requirePasswordChange']) {
    localStorage.removeItem(key);
  }
};

export const AuthProvider = ({ children }) => {
  // Before any child renders or calls the API
  useState(dropLeftoverSessionOnSso);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Tell the rest of the app (BrandingContext) when the signed-in person or business changes
  useEffect(() => {
    window.dispatchEvent(new Event(SESSION_CHANGED));
  }, [isAuthenticated, user?.id]);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(DEFAULT_SESSION_TIMEOUT);
  const inactivityTimerRef = useRef(null);
  const logoutCalledRef = useRef(false);
  const { setTheme } = useTheme();

  // Each user has their own theme, saved on their account: apply it whenever the signed-in
  // user changes (sign-in, reload, switching user on a shared till). No choice yet: Light.
  const userId = user?.id;
  const userTheme = user?.theme_preference;
  useEffect(() => {
    if (userId === undefined) return;
    setTheme(userTheme || 'light');
  }, [userId, userTheme, setTheme]);

  // Check if user is logged in on mount (skip on public pages)
  useEffect(() => {
    const publicPaths = ['/login', '/forgot-password', '/reset-password', '/demo-login', '/sso', '/session-ended'];
    const currentPath = window.location.pathname;

    // Skip auth check on public pages to prevent unnecessary API calls
    if (!publicPaths.some((path) => currentPath.startsWith(path))) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        const saved = JSON.parse(savedUser);
        setUser(saved);
        setIsAuthenticated(true);

        // Verify token is still valid, and take the role's current permissions. What only the
        // sign-in knew (a hub sign-in's is_sso, tenantRole, modules) is kept.
        try {
          const response = await authAPI.getCurrentUser();
          const current = { ...saved, ...response.data };
          setUser(current);
          localStorage.setItem('user', JSON.stringify(current));

          // Fetch security settings for session timeout
          fetchSessionTimeout();
        } catch {
          // Token invalid, clear storage
          logout('expired');
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch session timeout from security settings
  const fetchSessionTimeout = async () => {
    try {
      const response = await securitySettingsAPI.getSettings();
      if (response.success && response.data?.session_timeout_minutes) {
        setSessionTimeoutMinutes(response.data.session_timeout_minutes);
      }
    } catch (error) {
      // Use default timeout if fetch fails
      console.error('Failed to fetch security settings:', error);
    }
  };

  // Inactivity-based auto-logout
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    // Store last activity time for cross-tab detection
    localStorage.setItem('lastActivity', Date.now().toString());

    inactivityTimerRef.current = setTimeout(
      () => {
        if (logoutCalledRef.current) return;
        logoutCalledRef.current = true;
        toast.warning('Session expired due to inactivity');
        logout('inactive');
      },
      sessionTimeoutMinutes * 60 * 1000
    );
  }, [sessionTimeoutMinutes]);

  // Set up / tear down activity listeners when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timer when not authenticated
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    logoutCalledRef.current = false;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    // Check if we were inactive while the tab was closed/hidden
    const lastActivity = localStorage.getItem('lastActivity');
    if (lastActivity) {
      const elapsed = Date.now() - parseInt(lastActivity);
      if (elapsed > sessionTimeoutMinutes * 60 * 1000) {
        toast.warning('Session expired due to inactivity');
        logout('inactive');
        return;
      }
    }

    // Start the inactivity timer
    resetInactivityTimer();

    const handleActivity = () => resetInactivityTimer();
    events.forEach((event) => window.addEventListener(event, handleActivity, { passive: true }));

    return () => {
      events.forEach((event) => window.removeEventListener(event, handleActivity));
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [isAuthenticated, resetInactivityTimer]);

  const login = async (email, password) => {
    try {
      console.log('Login attempt for:', email);
      const response = await authAPI.login(email, password);
      console.log('Login response:', response);

      // Handle require_password_change case
      if (response.require_password_change) {
        const { user, accessToken, refreshToken } = response;

        // Store tokens and user data
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('user', JSON.stringify(user));
        startActivityClock();
        localStorage.setItem('requirePasswordChange', 'true');
        clearHubSession(); // signed in with this app's own account

        setUser(user);
        setIsAuthenticated(true);

        toast.warning(response.message || 'Password change required on first login');
        return {
          success: true,
          user,
          requirePasswordChange: true
        };
      }

      // Normal login response
      const { user, accessToken, refreshToken } = response.data || response;

      // Store tokens and user data
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      startActivityClock();
      clearHubSession(); // signed in with this app's own account

      setUser(user);
      setIsAuthenticated(true);

      // Fetch security settings for session timeout
      fetchSessionTimeout();

      toast.success(`Welcome back, ${user.full_name}!`);
      return { success: true, user };
    } catch (error) {
      console.error('Login error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || 'Login failed';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  /** Ends the session; `reason` is what the session-ended screen tells a hub user. */
  const logout = async (reason) => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      // Only call logout API for non-demo users
      if (refreshToken && !user?.is_demo) {
        await authAPI.logout(refreshToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear storage and state
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('activeModule'); // Clear module selection on logout
      localStorage.removeItem('requirePasswordChange');
      localStorage.removeItem('lastActivity');
      localStorage.removeItem('tenant');

      setUser(null);
      setIsAuthenticated(false);

      // People who came from One Manager see why it ended, with a button back there (a click
      // handler passes an event, which means they signed out themselves)
      if (!leaveSignedOut(typeof reason === 'string' ? reason : 'signed-out')) toast.info('Logged out successfully');
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  /**
   * Switch to a theme and save it as the signed-in user's choice. The theme changes at
   * once; if it cannot be saved, the previous theme comes back.
   */
  const chooseTheme = async (themeId) => {
    setTheme(themeId);
    if (!user) return;
    const previous = user;
    updateUser({ ...user, theme_preference: themeId });
    if (user.is_demo) return;
    try {
      await authAPI.updateTheme(themeId);
    } catch (error) {
      console.error('Failed to save theme:', error);
      updateUser(previous); // the effect above puts the previous theme back
      toast.error('Could not save your theme. Please try again.');
    }
  };

  /**
   * Demo login - for demo mode access
   * @param {string} token - JWT token from demo login
   * @param {object} demoUser - User object from demo login response
   */
  const demoLogin = (token, demoUser) => {
    // Store token and user data
    localStorage.setItem('accessToken', token);
    localStorage.setItem('user', JSON.stringify(demoUser));
    startActivityClock();

    setUser(demoUser);
    setIsAuthenticated(true);
  };

  /**
   * SSO login - for One Manager Platform SSO access
   * @param {string} token - JWT token from SSO login
   * @param {object} ssoUser - User object from SSO login response
   * @param {object} tenant - Tenant object from SSO login response
   */
  const ssoLogin = (token, ssoUser, tenant) => {
    // Store token and user data
    localStorage.setItem('accessToken', token);
    localStorage.setItem('user', JSON.stringify(ssoUser));
    startActivityClock();
    localStorage.setItem('tenant', JSON.stringify(tenant));
    markHubSession();

    setUser(ssoUser);
    setIsAuthenticated(true);
  };

  // Role helper functions - support both old and new role names
  const ADMIN_ROLES = ['super_admin', 'system_admin', 'retail_admin', 'branch_admin', 'admin'];
  const STAFF_ROLES = ['branch_staff', 'staff'];

  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const isStaff = STAFF_ROLES.includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'system_admin';

  // Granular Permission Helpers
  const hasPermission = useCallback(
    (permissionKey) => {
      if (!user) return false;
      if (isSuperAdmin) return true;
      const userPermissions = user.permissions || {};
      return userPermissions[permissionKey] === true || userPermissions['admin'] === true;
    },
    [user, isSuperAdmin]
  );

  const hasAnyPermission = useCallback(
    (permissionKeys = []) => {
      if (!user) return false;
      if (isSuperAdmin) return true;
      const userPermissions = user.permissions || {};
      return permissionKeys.some(
        (key) => userPermissions[key] === true || userPermissions['admin'] === true
      );
    },
    [user, isSuperAdmin]
  );

  const hasAllPermissions = useCallback(
    (permissionKeys = []) => {
      if (!user) return false;
      if (isSuperAdmin) return true;
      const userPermissions = user.permissions || {};
      return permissionKeys.every(
        (key) => userPermissions[key] === true || userPermissions['admin'] === true
      );
    },
    [user, isSuperAdmin]
  );

  const value = {
    user,
    loading,
    isAuthenticated,
    isAdmin,
    isStaff,
    isSuperAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    login,
    logout,
    updateUser,
    chooseTheme,
    checkAuth,
    demoLogin,
    ssoLogin
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
